import i18n from 'i18next';
import React from 'react';
import styled from 'styled-components';

import { iiafe } from '../../../utils/async';
import { initializeI18n } from '../../../utils/i18-next';

type Props = {
  height100Percent?: boolean;
  children: React.ReactNode;
};

enum Status {
  Error,
  Loading,
  Success,
}

// Shared init promise so StrictMode double-mount only runs init once (avoids "i18next is already initialized" warning).
let initPromise: Promise<void> | null = null;

const getInitPromise = (): Promise<void> => {
  if (i18n.isInitialized) {
    return Promise.resolve();
  }
  if (!initPromise) {
    initPromise = initializeI18n();
  }
  return initPromise;
};

/**
 * Sets up i18next and wraps children with a div element with class "notranslate" if selected
 * language is either 'sv', 'en', or 'de'.
 *
 * Wrap this component around the components you want to add translation for. TODO: Download
 * language files as part of the login process and set up i18next once.
 */
const LanguageProvider = ({ height100Percent = true, ...props }: Props) => {
  const [status, setStatus] = React.useState(i18n.isInitialized ? Status.Success : Status.Loading);

  React.useEffect(() => {
    iiafe(async () => {
      try {
        await getInitPromise();
        setStatus(Status.Success);
      } catch (error) {
        setStatus(Status.Error);
      }
    });
  }, []);

  switch (status) {
    case Status.Error:
      return <Component>Could not load translation files.</Component>;
    case Status.Loading:
      return <Component>Loading translation files...</Component>;
    case Status.Success: {
      return (
        // 100% height might conflict with CSS so make it optional.
        // TODO: This div can be removed when Google Translate is removed, and only render the
        // content as is.
        <div className="notranslate" style={{ height: height100Percent ? '100%' : undefined }}>
          {props.children}
        </div>
      );
    }
  }
};

const Component = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 1000;
  background-color: #0006;
  color: #fff;
  display: grid;
  place-content: center;
`;

export { LanguageProvider };
